import { createContext, useContext, useMemo, useState, type ReactNode } from "react"

/**
 * Which part of the screen the guided tour is talking about right now.
 *
 * The tiles live in the page and the Home / Ask-for-help buttons live in the
 * layout, so the two can't be wired together by props — the layout renders the
 * page, not the other way round. This context lets the page (which owns the
 * video) say what's being narrated, and lets both the page and the layout light
 * up their own piece.
 *
 * `chrome` is the top bar. It's the one target with nothing to highlight inside
 * the page, and the one that needs the window scrolled back up: Emma points at
 * the top bar last, by which time the driver has been scrolled down to the
 * bottom tile.
 */
export type TourTarget = "documents" | "profile" | "calendar" | "forms" | "refer" | "chrome"

type Spotlight = {
  target: TourTarget | null
  setTarget: (target: TourTarget | null) => void
}

const TourSpotlightContext = createContext<Spotlight>({ target: null, setTarget: () => {} })

export function TourSpotlightProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<TourTarget | null>(null)
  const value = useMemo(() => ({ target, setTarget }), [target])

  return <TourSpotlightContext.Provider value={value}>{children}</TourSpotlightContext.Provider>
}

export function useTourSpotlight() {
  return useContext(TourSpotlightContext)
}

/**
 * The ring worn by whatever the tour is currently talking about. Decorative —
 * it repeats what the narrator is saying out loud, so it carries no ARIA and
 * means nothing on its own.
 */
export const TOUR_RING = "ring-2 ring-primary ring-offset-2 ring-offset-background"
